import { ALL_BRANCHES, TenantContext } from 'src/context/types';

/**
 * Returns the next full tenant context for a scope switch; callers hand it to
 * setContext(). Empty strings signal "resolve defaults" to the caller
 * (org/restaurant lists are in scope there).
 */
/**
 * The branch to adopt when moving to another restaurant. The current branch belongs to
 * the restaurant being left, so the caller resolves the target's own branch list first.
 */
export function pickDefaultBranchId(branches: { id: string }[] | undefined, canAggregate: boolean): string {
  const first = branches?.[0]?.id;
  if (first) {
    return first;
  }
  return canAggregate ? ALL_BRANCHES : '';
}

export type SwitchOptions = {
  /** Whether the caller's org role may use `/b/all`; owner and admin only. */
  canAggregate?: boolean;
};

export function switchScope(
  current: TenantContext,
  next: Partial<TenantContext>,
  options: SwitchOptions = {}
): TenantContext {
  if (next.orgSlug && next.orgSlug !== current.orgSlug) {
    return { orgSlug: next.orgSlug, restaurantId: '', branchId: '' };
  }
  if (next.restaurantId && next.restaurantId !== current.restaurantId) {
    // The current branch belongs to the old restaurant, so it cannot come along. The
    // caller normally resolves the target's first branch and passes it; without one we
    // fall back to the aggregate for owner/admin, and to "" — which reads back as no
    // cookie, so ContextLoader re-resolves defaults — for a member, who would be
    // rejected with BRANCH_SCOPE_FORBIDDEN on `all`.
    return {
      ...current,
      restaurantId: next.restaurantId,
      branchId: next.branchId ?? (options.canAggregate ? ALL_BRANCHES : ''),
    };
  }
  return { ...current, ...next };
}
